import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../i18n';
import { VideoCard } from '../components/VideoCard';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Heart, Eye, UserPlus, UserMinus, Users, MessageCircle, Check, Instagram, Music2, Twitter, Youtube } from 'lucide-react';
import { toast } from 'sonner';

export default function UserPublicPage() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const t = useT();
    const { user: currentUser } = useAuth();
    const [data, setData] = useState(null);
    const [followBusy, setFollowBusy] = useState(false);
    // Followers / following modal
    const [socialTab, setSocialTab] = useState(null); // 'followers' | 'following' | null
    const [social, setSocial] = useState({ followers: [], following: [] });
    // Chat request dialog (one message to a non-friend)
    const [msgOpen, setMsgOpen] = useState(false);
    const [msgText, setMsgText] = useState('');
    const [msgBusy, setMsgBusy] = useState(false);
    const [msgSent, setMsgSent] = useState(false);

    useEffect(() => {
        setData(null);
        setMsgSent(false);
        setSocialTab(null);
        (async () => {
            try {
                const r = await api.get(`/users/${userId}`);
                setData(r.data);
            } catch {}
        })();
    }, [userId]);

    const toggleFollow = async () => {
        if (!currentUser) { navigate('/entrar'); return; }
        setFollowBusy(true);
        try {
            if (data.is_following) await api.delete(`/users/${userId}/follow`);
            else await api.post(`/users/${userId}/follow`);
            const fresh = await api.get(`/users/${userId}`);
            setData(fresh.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut actualitzar el seguiment');
        } finally {
            setFollowBusy(false);
        }
    };

    const openSocial = async (tab) => {
        setSocialTab(tab);
        try {
            const r = await api.get(`/users/${userId}/social`);
            setSocial({ followers: r.data.followers || [], following: r.data.following || [] });
        } catch {}
    };

    const toggleListFollow = async (u) => {
        if (!currentUser) { navigate('/entrar'); return; }
        try {
            if (u.is_following) await api.delete(`/users/${u.user_id}/follow`);
            else await api.post(`/users/${u.user_id}/follow`);
            setSocial((cur) => ({
                followers: cur.followers.map((x) => x.user_id === u.user_id ? { ...x, is_following: !x.is_following } : x),
                following: cur.following.map((x) => x.user_id === u.user_id ? { ...x, is_following: !x.is_following } : x),
            }));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut actualitzar el seguiment');
        }
    };

    const openMessageDialog = () => {
        if (!currentUser) { navigate('/entrar'); return; }
        setMsgOpen(true);
    };

    const sendRequest = async (e) => {
        e.preventDefault();
        if (!msgText.trim() || msgBusy) return;
        setMsgBusy(true);
        try {
            const form = new FormData();
            form.append('content', msgText.trim());
            await api.post(`/friends/${userId}/request`, form);
            setMsgText('');
            setMsgSent(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'No s\u2019ha pogut enviar la sol\u00b7licitud');
        } finally {
            setMsgBusy(false);
        }
    };

    if (!data) return <main className="p-8 text-center text-[color:var(--alb-text-muted)]">Carregant...</main>;
    const { user, videos } = data;
    const socialList = socialTab ? (social[socialTab] || []) : [];
    const socialLinks = [
        { name: 'Instagram', value: user.social_links?.instagram, url: (v) => v.startsWith('http') ? v : `https://instagram.com/${v.replace(/^@/, '')}`, Icon: Instagram },
        { name: 'TikTok', value: user.social_links?.tiktok, url: (v) => v.startsWith('http') ? v : `https://www.tiktok.com/@${v.replace(/^@/, '')}`, Icon: Music2 },
        { name: 'X', value: user.social_links?.twitter, url: (v) => v.startsWith('http') ? v : `https://x.com/${v.replace(/^@/, '')}`, Icon: Twitter },
        { name: 'YouTube', value: user.social_links?.youtube, url: (v) => v.startsWith('http') ? v : `https://www.youtube.com/@${v.replace(/^@/, '')}`, Icon: Youtube },
    ].filter((link) => link.value);
    return (
        <main className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-6 md:py-10 pb-24 md:pb-12" data-testid="user-public-page">
            <section className="flex flex-col items-start gap-4 mb-6 sm:flex-row sm:items-center">
                <Avatar className="h-16 w-16 ring-2 ring-[color:var(--alb-sun)]">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)] text-2xl">{(user.name || 'U').slice(0,1).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                    <h1 className="font-display text-2xl font-semibold">{user.name}</h1>
                    {user.bio && <p className="mt-1 max-w-2xl whitespace-pre-wrap text-sm text-[color:var(--alb-text-muted)]">{user.bio}</p>}
                    <div className="flex gap-3 text-sm mt-1">
                        <span className="inline-flex items-center gap-1 text-[color:var(--alb-pink)] font-mono-tab"><Heart className="h-4 w-4 fill-[color:var(--alb-pink)]" /> {user.total_likes}</span>
                        <span className="inline-flex items-center gap-1 text-[color:var(--alb-aqua)] font-mono-tab"><Eye className="h-4 w-4" /> {user.total_views}</span>
                        <button type="button" onClick={() => openSocial('followers')} data-testid="user-followers-button" className="inline-flex items-center gap-1 font-mono-tab hover:underline">
                            <Users className="h-4 w-4" /> {data.followers_count} {t('user.followers')}
                        </button>
                        <button type="button" onClick={() => openSocial('following')} data-testid="user-following-button" className="font-mono-tab hover:underline">
                            {data.following_count} {t('user.following')}
                        </button>
                    </div>
                    {socialLinks.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2" data-testid="user-social-links">
                            {socialLinks.map(({ name, value, url, Icon }) => (
                                <a key={name} href={url(value)} target="_blank" rel="noreferrer" title={value} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--alb-hairline)] px-2.5 py-1 text-xs hover:border-[color:var(--alb-aqua)]">
                                    <Icon className="h-3.5 w-3.5" /> {name}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
                {currentUser?.user_id !== userId && (
                    <div className="flex flex-wrap gap-2">
                        <Button disabled={followBusy} variant={data.is_following ? 'outline' : 'default'} onClick={toggleFollow} className={data.is_following ? '' : 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]'} data-testid="user-follow-button">
                            {data.is_following ? <UserMinus className="mr-2 h-4 w-4" /> : <UserPlus className="mr-2 h-4 w-4" />}
                            {data.is_following ? (data.is_mutual ? 'Amics · Deixar de seguir' : 'Només seguir · Deixar de seguir') : t('user.follow')}
                        </Button>
                        {data.is_mutual ? (
                            <Button variant="outline" onClick={() => navigate('/amics')} data-testid="user-chat-button">
                                <MessageCircle className="mr-2 h-4 w-4" /> {t('user.chat_open')}
                            </Button>
                        ) : (
                            <Button variant="outline" onClick={openMessageDialog} disabled={msgSent} data-testid="user-message-button">
                                <MessageCircle className="mr-2 h-4 w-4" /> {msgSent ? t('user.request_sent') : t('user.send_message')}
                            </Button>
                        )}
                    </div>
                )}
            </section>
            {videos.length === 0 ? (
                <div className="text-[color:var(--alb-text-muted)] text-sm">Sense vídeos publicats.</div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {videos.map((v) => <VideoCard key={v.video_id} video={v} />)}
                </div>
            )}

            <Dialog open={!!socialTab} onOpenChange={(open) => { if (!open) setSocialTab(null); }}>
                <DialogContent className="bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                    <DialogHeader>
                        <DialogTitle>{socialTab === 'followers' ? t('user.followers_title') : t('user.following_title')}</DialogTitle>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1" data-testid="user-social-list">
                        {socialList.length === 0 && <p className="text-sm text-[color:var(--alb-text-muted)] py-4 text-center">—</p>}
                        {socialList.map((u) => (
                            <div key={u.user_id} className="flex items-center gap-3">
                                <button type="button" className="flex items-center gap-3 min-w-0 flex-1 text-left" onClick={() => { setSocialTab(null); navigate(`/usuari/${u.user_id}`); }}>
                                    <Avatar className="h-9 w-9">
                                        <AvatarImage src={u.picture} />
                                        <AvatarFallback className="bg-[color:var(--alb-surface-2)] text-[color:var(--alb-sun)]">{(u.name || 'U').slice(0, 1).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <span className="font-semibold truncate">{u.name}</span>
                                </button>
                                {u.user_id !== currentUser?.user_id && (
                                    <Button size="sm" variant={u.is_following ? 'outline' : 'default'} onClick={() => toggleListFollow(u)} className={u.is_following ? '' : 'bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]'}>
                                        {u.is_following ? t('user.unfollow') : t('user.follow')}
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                <DialogContent className="bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                    <DialogHeader>
                        <DialogTitle>{t('user.request_title')}</DialogTitle>
                        <DialogDescription>{t('user.request_subtitle')}</DialogDescription>
                    </DialogHeader>
                    {msgSent ? (
                        <div className="py-4 text-center">
                            <Check className="mx-auto h-8 w-8 text-[color:var(--alb-aqua)]" />
                            <p className="mt-2 font-semibold">{t('user.request_sent')}</p>
                            <Button className="mt-4 bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" onClick={() => setMsgOpen(false)}>OK</Button>
                        </div>
                    ) : (
                        <form onSubmit={sendRequest}>
                            <Textarea
                                value={msgText}
                                onChange={(e) => setMsgText(e.target.value)}
                                placeholder={t('user.request_placeholder')}
                                maxLength={2000}
                                rows={4}
                                className="bg-[color:var(--alb-ink-2)]"
                                data-testid="user-request-input"
                            />
                            <DialogFooter className="mt-3">
                                <Button type="submit" disabled={msgBusy || !msgText.trim()} className="bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)]" data-testid="user-request-send">
                                    {t('user.request_send')}
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </main>
    );
}
